import { UpdateEventActivityPayload } from 'app/frontend/base/api/event'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'

type TeamWithMembers = {
  members?: Array<{ characterReference: wowaudit.CharacterReference }>
}

export type SelectionUpdate = {
  eventParticipantId: number
  role?: string
  specId?: number | null
  characterId?: number
  selected?: boolean
  activityId?: number
}

export type ActivitySelectionUpdate = {
  activityId: number
  selected: boolean
}

export type AllActivitiesSelectionUpdate = {
  activityIds: number[]
  selected: boolean
}

export type ActivityUpdate = {
  activityId: number
  eventActivity: UpdateEventActivityPayload
  optimisticPlanOverrides?: wowaudit.EventActivity['planOverrides']
}

export type ActivityOrderUpdate = {
  activityOrder: number[]
}

const findCharacterReference = (team: TeamWithMembers, characterId: number) =>
  team.members?.find((member) => member.characterReference.id === characterId)
    ?.characterReference

export const applySelection = (
  event: wowaudit.Event,
  {
    eventParticipantId,
    role,
    specId,
    characterId,
    selected,
    activityId,
  }: SelectionUpdate,
  team: TeamWithMembers,
): wowaudit.Event => ({
  ...event,
  participants: event.participants.map((participant) => {
    if (participant.id !== eventParticipantId) return participant

    const nextParticipant: wowaudit.EventParticipant = { ...participant }
    if (activityId === undefined) {
      if (role !== undefined) {
        nextParticipant.role = role as wowaudit.EventParticipant['role']
      }
      if (specId !== undefined) {
        if (specId === null) {
          delete nextParticipant.specId
        } else {
          nextParticipant.specId = specId
        }
      }
      if (characterId !== undefined) {
        nextParticipant.altCharacterId = characterId
        nextParticipant.altCharacterReference = findCharacterReference(
          team,
          characterId,
        )
      }
    }

    const nextSelections = { ...nextParticipant.selections }
    const applyActivitySelection = (targetActivityId: number) => {
      const key = targetActivityId.toString()
      const currentSelection = nextSelections[key] ?? {}
      const updatedSelection = { ...currentSelection }
      if (selected !== undefined) updatedSelection.selected = selected
      if (role !== undefined) updatedSelection.role = role
      if (specId !== undefined) updatedSelection.spec_id = specId
      if (characterId !== undefined) updatedSelection.character_id = characterId
      nextSelections[key] = updatedSelection
    }

    if (activityId !== undefined) {
      applyActivitySelection(activityId)
    } else if (
      selected !== undefined ||
      role !== undefined ||
      specId !== undefined ||
      characterId !== undefined
    ) {
      event.activities.forEach((activity) =>
        applyActivitySelection(activity.id),
      )
    }

    nextParticipant.selections = nextSelections
    return nextParticipant
  }),
  participantReferences: event.participantReferences.map(
    (participantReference) => {
      if (participantReference.id !== eventParticipantId)
        return participantReference

      const nextParticipantReference: wowaudit.EventParticipantReference = {
        ...participantReference,
      }
      if (activityId === undefined) {
        if (role !== undefined) {
          nextParticipantReference.role =
            role as wowaudit.EventParticipantReference['role']
        }
        if (specId !== undefined) {
          if (specId === null) {
            delete nextParticipantReference.specId
          } else {
            nextParticipantReference.specId = specId
          }
        }
        if (characterId !== undefined) {
          nextParticipantReference.altCharacterId = characterId
          nextParticipantReference.altCharacterReference =
            findCharacterReference(team, characterId)
        }
      }
      return nextParticipantReference
    },
  ),
})

export const applyParticipantStatus = (
  event: wowaudit.Event,
  eventParticipantId: number,
  status: wowaudit.EventParticipantReference['status'],
): wowaudit.Event => ({
  ...event,
  participants: event.participants.map((participant) =>
    participant.id === eventParticipantId
      ? { ...participant, status }
      : participant,
  ),
  participantReferences: event.participantReferences.map(
    (participantReference) =>
      participantReference.id === eventParticipantId
        ? { ...participantReference, status }
        : participantReference,
  ),
})

export const applyParticipantAdminComment = (
  event: wowaudit.Event,
  eventParticipantId: number,
  adminComment: string,
): wowaudit.Event => ({
  ...event,
  participants: event.participants.map((participant) =>
    participant.id === eventParticipantId
      ? { ...participant, adminComment }
      : participant,
  ),
  participantReferences: event.participantReferences.map(
    (participantReference) =>
      participantReference.id === eventParticipantId
        ? { ...participantReference, adminComment }
        : participantReference,
  ),
})

export const applyActivitySelectionForAll = (
  event: wowaudit.Event,
  { activityId, selected }: ActivitySelectionUpdate,
): wowaudit.Event => ({
  ...event,
  participants: event.participants.map((participant) => {
    if (unavailableParticipantStatuses.includes(participant.status)) {
      return participant
    }

    return {
      ...participant,
      selections: {
        ...participant.selections,
        [activityId]: {
          ...(participant.selections[activityId] ?? {}),
          selected,
        },
      },
    }
  }),
})

export const applyAllActivitiesSelection = (
  event: wowaudit.Event,
  { activityIds, selected }: AllActivitiesSelectionUpdate,
): wowaudit.Event => ({
  ...event,
  participants: event.participants.map((participant) => {
    if (unavailableParticipantStatuses.includes(participant.status)) {
      return participant
    }

    const nextSelections = { ...participant.selections }
    activityIds.forEach((id) => {
      nextSelections[id] = {
        ...(nextSelections[id] ?? {}),
        selected,
      }
    })

    return { ...participant, selections: nextSelections }
  }),
})

export const applyActivityUpdate = (
  event: wowaudit.Event,
  { activityId, eventActivity, optimisticPlanOverrides }: ActivityUpdate,
): wowaudit.Event => {
  const nextPlanOverrides =
    optimisticPlanOverrides ?? eventActivity.planOverrides
  const normalizedActivityUpdate: Partial<wowaudit.EventActivity> = {
    ...(eventActivity.active === undefined
      ? {}
      : { active: eventActivity.active }),
    ...(eventActivity.order === undefined
      ? {}
      : { order: eventActivity.order }),
    ...(eventActivity.linkedPlans === undefined
      ? {}
      : { linkedPlans: eventActivity.linkedPlans }),
    ...(eventActivity.difficulty === undefined
      ? {}
      : { difficulty: eventActivity.difficulty }),
    ...(eventActivity.raidSize === undefined
      ? {}
      : { raidSize: eventActivity.raidSize }),
    ...(eventActivity.notes === undefined
      ? {}
      : { notes: eventActivity.notes }),
    ...(nextPlanOverrides === undefined
      ? {}
      : { planOverrides: nextPlanOverrides }),
  }
  return {
    ...event,
    activities: event.activities.map((activity) =>
      activity.id === activityId
        ? { ...activity, ...normalizedActivityUpdate }
        : activity,
    ),
  }
}

export const applyActivityOrder = (
  event: wowaudit.Event,
  { activityOrder }: ActivityOrderUpdate,
): wowaudit.Event => {
  const orderByActivityId = new Map<number, number>()
  activityOrder.forEach((activityId, index) => {
    orderByActivityId.set(activityId, index)
  })
  const sortedActivities = event.activities
    .slice()
    .sort((left, right) => left.order - right.order)
  const missingActivityIds = sortedActivities
    .map((activity) => activity.id)
    .filter((activityId) => !orderByActivityId.has(activityId))
  missingActivityIds.forEach((activityId, index) => {
    orderByActivityId.set(activityId, activityOrder.length + index)
  })
  return {
    ...event,
    activities: event.activities.map((activity) => ({
      ...activity,
      order: orderByActivityId.get(activity.id) as number,
    })),
  }
}
